import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, CircleNotch, HandCoins, TrendUp } from "@phosphor-icons/react";
import { api, describeApiError } from "@/lib/api";
import { VendorInsightCard } from "@/components/VendorInsightCard";
import { VendorNegotiationDialog } from "@/components/VendorNegotiationDialog";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function VendorInsightsPanel({ businessId = null, title = "Vendor Insights", subtitle = "", testIdPrefix = "vendor-insights", showBusinessName = false, maxItems = null }) {
  const storageKey = `bk_vendor_threshold_${businessId || "all"}`;
  const initialThreshold = useMemo(() => {
    const saved = Number(localStorage.getItem(storageKey) || 1000);
    return Number.isFinite(saved) && saved >= 0 ? saved : 1000;
  }, [storageKey]);
  const [threshold, setThreshold] = useState(initialThreshold);
  const [thresholdInput, setThresholdInput] = useState(String(initialThreshold));
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeInsight, setActiveInsight] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    localStorage.setItem(storageKey, String(threshold));
  }, [storageKey, threshold]);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ threshold: String(threshold) });
    if (businessId) params.set("business_id", businessId);
    setLoading(true);
    api.get(`/vendor-insights?${params.toString()}`)
      .then((r) => { if (!cancelled) setPayload(r.data); })
      .catch((err) => { if (!cancelled) toast.error(describeApiError(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [businessId, threshold, refreshKey]);

  const applyThreshold = () => {
    const next = Number(thresholdInput);
    if (!Number.isFinite(next) || next < 0) return toast.error("Enter a valid spend threshold");
    setThreshold(Math.round(next));
  };

  const insights = maxItems ? (payload?.insights || []).slice(0, maxItems) : (payload?.insights || []);

  return (
    <section className="space-y-5" data-testid={testIdPrefix}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Money Leak Detector</p>
          <h3 className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{title}</h3>
          <p className="text-sm mt-2 max-w-3xl" style={{ color: "var(--bk-text-2)" }} data-testid={`${testIdPrefix}-subtitle`}>
            {subtitle || payload?.summary?.rowan_summary || "Rowan watches repeat vendors so you can negotiate before cost creep becomes a habit."}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input value={thresholdInput} onChange={(e) => setThresholdInput(e.target.value)} type="number" min="0" step="50" data-testid={`${testIdPrefix}-threshold-input`} className="w-[150px] rounded-full border px-4 py-2.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
          <button onClick={applyThreshold} data-testid={`${testIdPrefix}-threshold-apply`} className="px-4 py-2.5 rounded-full border text-sm font-semibold" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
            Apply threshold
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bk-card p-8 flex items-center justify-center gap-2 text-sm" style={{ color: "var(--bk-text-2)" }} data-testid={`${testIdPrefix}-loading`}>
          <CircleNotch size={18} className="animate-spin" /> Rowan is sniffing through vendor spend…
        </div>
      ) : insights.length === 0 ? (
        <div className="bk-card p-8 text-center" data-testid={`${testIdPrefix}-empty`}>
          <MagnifyingGlass size={28} weight="duotone" color="var(--bk-accent)" className="mx-auto" />
          <p className="font-semibold mt-3" style={{ color: "var(--bk-text)" }}>No vendor leverage yet</p>
          <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }}>Once a vendor crosses your threshold or starts raising prices, Rowan will wave you over here.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bk-card p-5" data-testid={`${testIdPrefix}-summary-count`}>
              <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Watched vendors</p>
              <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{payload?.summary?.count || 0}</p>
            </div>
            <div className="bk-card p-5" data-testid={`${testIdPrefix}-summary-spend`}>
              <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Spend under review</p>
              <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{money(payload?.summary?.watched_annual_spend || 0)}</p>
            </div>
            <div className="bk-card p-5" data-testid={`${testIdPrefix}-summary-trend`}>
              <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Hot leads</p>
              <p className="font-display text-3xl font-bold mt-2 inline-flex items-center gap-2" style={{ color: "var(--bk-accent)" }}>
                <HandCoins size={20} weight="duotone" /> {payload?.summary?.loyalty_discount_count || 0}
              </p>
              <p className="text-xs mt-1 inline-flex items-center gap-1.5" style={{ color: "var(--bk-text-2)" }}>
                <TrendUp size={13} weight="duotone" /> {payload?.summary?.price_increase_count || 0} with rising prices
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            {insights.map((insight, index) => (
              <VendorInsightCard key={insight.id} insight={insight} index={index} showBusinessName={showBusinessName} onDraft={setActiveInsight} testIdPrefix={testIdPrefix} />
            ))}
          </div>
        </>
      )}

      <VendorNegotiationDialog
        open={!!activeInsight}
        onOpenChange={(openState) => { if (!openState) setActiveInsight(null); }}
        insight={activeInsight}
        threshold={threshold}
        testIdPrefix={testIdPrefix}
        onUpdated={() => setRefreshKey((n) => n + 1)}
      />
    </section>
  );
}